/** @jsxImportSource @emotion/react **/
import React, { FC, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Select from "react-select";

import { LANG_ALL, styleContainer } from "../../../pages/utils";
import { Context } from "../../../context/CardApplyContext";
import { BaseInput, Text, TextareaInput } from "../../atoms";
import { ContentSection } from "../../organisms";
import MediaField from "../MediaField";
import * as styles from "../Header/index.styles";

const CardApplyForm: FC = () => {
  const {
    formData,
    handleChangeFormData: handleChange,
    handleUpdateFormData: handleData,
  } = useContext(Context);

  const { id } = useParams();
  const detail_data = useSelector((state: any) => state.get_card_apply.data);
  const news_data = id ? detail_data?.data.find((r: any) => r.id === id) : null;
  const howToApply = useSelector((state: any) => state.get_how_to_apply.data);

  const [howToApplyOptions, setHowToApplyOptions] = useState<any>([]);

  type HowToApplyExtract = {
    id: string;
    title: string;
    lang: string;
  };

  useEffect(() => {
    const howToApplyConstructed = howToApply.data?.map(
      ({ title, lang, id }: HowToApplyExtract) => ({
        label: `${title} - ${lang}`,
        value: id,
      })
    );

    setHowToApplyOptions(howToApplyConstructed || []);

    if (!!id) {
      handleData(news_data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, news_data, howToApply]);

  const langInit = news_data?.lang
    ? { value: news_data.lang, label: news_data.lang }
    : { value: "", label: "" };

  const howToApplyInit = news_data?.apply?.id
    ? {
        value: news_data.apply.id,
        label: `${news_data.apply.title} - ${news_data.apply.lang}`,
      }
    : { value: "", label: "" };

  const [isTitleValid, setTitleValid] = useState<boolean>(false);
  const [isDescriptionValid, setDescriptionValid] = useState<boolean>(false);
  const [isHrefValid, setHrefValid] = useState<boolean>(false);
  const [buttonValid, setButtonValid] = useState<boolean>(false);

  useEffect(() => {
    setTitleValid(formData.title.length > 2);
  }, [formData.title]);

  useEffect(() => {
    setDescriptionValid(formData.description.length > 0);
  }, [formData.description]);

  useEffect(() => {
    setHrefValid(formData.href.length > 0);
  }, [formData.href]);

  useEffect(() => {
    setButtonValid(formData.button.length > 0);
  }, [formData.button]);

  const handleLang = (e: any) => {
    handleChange({ lang: e.value });
  };

  return (
    <ContentSection
      style={styleContainer}
      header={
        <React.Fragment>
          <Text textType="h4" weight="black">
            {id ? "Editar" : "Nuevo"} Tarjeta de aplicación
          </Text>
        </React.Fragment>
      }
    >
      <BaseInput
        label="Título"
        value={formData.title}
        onChange={(value) => handleChange({ title: value })}
        placeholder="Escribe tu título"
        maxLength={500}
        error={!isTitleValid && formData.title.length > 0}
        success={isTitleValid}
        hint={
          !isTitleValid && formData.title.length > 0
            ? "Digite más de dos letras"
            : ""
        }
      />

      <TextareaInput
        label="Descripción"
        value={formData.description}
        onChange={(value) => handleChange({ description: value })}
        placeholder="Escribe tu descripción"
        maxLength={500}
        error={!isDescriptionValid && formData.description.length > 0}
        success={isDescriptionValid}
        hint={
          !isDescriptionValid && formData.description.length > 0
            ? "Digite más de una letra"
            : ""
        }
      />

      <BaseInput
        label="Ir a"
        value={formData.href}
        onChange={(value) => handleChange({ href: value })}
        placeholder="Escribe la ruta del botón"
        maxLength={500}
        error={!isHrefValid && formData.href.length > 0}
        success={isHrefValid}
        hint={
          !isHrefValid && formData.href.length > 0
            ? "Digite más de una letra"
            : ""
        }
      />

      <BaseInput
        label="Título del botón"
        value={formData.button}
        onChange={(value) => handleChange({ button: value })}
        placeholder="Escribe un nombre para el botón"
        maxLength={500}
        error={!buttonValid && formData.button.length > 0}
        success={buttonValid}
        hint={
          !buttonValid && formData.button.length > 0
            ? "Digite más de una letra"
            : ""
        }
      />

      <MediaField
        label="Imagen"
        value={formData.image}
        folder="card-apply"
        onChange={(url) => handleChange({ image: url })}
      />

      <Text textType="label">
        COMO APLICAR:
        <Select
          css={styles.input}
          options={howToApplyOptions}
          defaultValue={howToApplyInit}
          onChange={(e: any) => handleChange({ apply: e.value })}
        />
      </Text>

      <Text textType="label">
        IDIOMA:{" "}
        <Select
          css={styles.input}
          options={LANG_ALL}
          defaultValue={langInit}
          onChange={(e) => handleLang(e)}
        />
      </Text>
    </ContentSection>
  );
};

export default CardApplyForm;